import styles from "./Tabs.module.css";

interface TabOption<T extends string> {
  value: T;
  label: string;
}

interface TabsProps<T extends string> {
  options: readonly TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "md" | "sm";
  ariaLabel?: string;
}

/**
 * Tabs — segmented control. Active option gets `--surface-well`.
 *
 * Used for the Activity status filter (All · Pending · Completed ·
 * Failed · Reversed) and the chart timeframe (1H · 1D · 1W · 1M · 1Y · All).
 */
export function Tabs<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  ariaLabel,
}: TabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`${styles.tabs} ${size === "sm" ? styles.sm : ""}`}
    >
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={`${styles.option} ${isActive ? styles.active : ""}`}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
